import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, combineLatest, from, Observable } from 'rxjs';
import { AlarmMessage } from './alarm-message';
import { WebsocketService } from '../shared/websocket/websocket.service';
import { WebsocketMessage } from '../shared/websocket/websocketMessage';
import { HttpClient } from '@angular/common/http';
import { AppMessageService } from '../shared/app-message/app-message.service';
import { AppMessage } from '../shared/app-message/app-message';
import { _, getDataName, parseToDate } from '../shared/utils';
import { environment } from '../../environments/environment';
import { format } from 'date-fns/esm';
import { BackendAlarm } from './alarm-definitions/backend-alarm';
import { AuthService } from '../auth/auth.service';
import { TranslateModel } from '../shared/translate.model';
import { TranslateService } from '@ngx-translate/core';
import { AbstractDataService } from '../shared/abstract-data.service';
import { AbstractDataModel } from '../shared/abstract-data.model';
import { ServiceAnswer } from '../shared/service-answer';
import { map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { getRoleGroupName } from '~app/custom/vrgroup/utils/personnel.utils';
import { DateFilters } from '../sidebar/filters/filters.model';
import * as Comlink from 'comlink';
import { StateService } from '~app/shared/state/state.service';
import { MapElement } from '~app/shared/map/map-element.model';
import { Geometry } from 'geojson';
import { LinkingService } from '~app/linking/linking.service';

export interface PaginationParams {
  page: number;
  offset: number;
  limit: number;
  dates?: DateFilters;
  alarmTypes: (string | number)[];
  active: boolean;
  trainTypes: (string | number)[];
}

interface WorkerAlarmFilterFunctions {
  checkIfAlarmPassesFilters: (alarm: BackendAlarm, filters: PaginationParams, tenant: number) => boolean;
  filterUserdisabled: (alarm: BackendAlarm, filters: PaginationParams) => boolean;
  filterUserdisabledAlarms: (alarms: AlarmMessage<BackendAlarm>[]) => AlarmMessage<BackendAlarm>[];
  filterByTrainType: (alarm: BackendAlarm, filteredTypes: string[]) => boolean;
  filterByUserdisabled: (alarm: BackendAlarm, filters: PaginationParams) => boolean;
  filterAlarms: (alarms: AlarmMessage<BackendAlarm>[], alarmFilters: PaginationParams, tenant: number) => AlarmMessage<BackendAlarm>[];
  filterPaginatedAlarms: (alarms: BackendAlarm[], filters: PaginationParams, tenant: number) => BackendAlarm[]
}

@Injectable()
export class AlarmService extends AbstractDataService<BackendAlarm> implements OnDestroy {

  private ngUnsubscribe = new Subject<void>();

  constructor(http: HttpClient,
    private readonly websocket: WebsocketService,
    private readonly authService: AuthService,
    private readonly state: StateService,
    private readonly appMessageService: AppMessageService,
    private readonly translateService: TranslateService,
    private readonly linkingService: LinkingService) {
    super(http, environment.dataApiURL + 'alarms/?');

    // Initialise after authentication
    authService.login$.pipe(take(1)).subscribe(() => {
      this.url = environment.dataApiURL + getDataName(this.authService.getTenant()) + 'Alarms/?';
      if (this.authService.getTenant() === 2) {
        this.url = environment.dataApiURL + 'alarms/?';
      }
      this.init();
    });
  }

  private alarms = new BehaviorSubject<AlarmMessage[]>([]);
  public alarms$ = this.alarms.asObservable();

  private paginatedAlarms = new BehaviorSubject<AbstractDataModel<AlarmMessage>>({ data: [], total: 0 });
  public paginatedAlarms$ = this.paginatedAlarms.asObservable();

  private alarmFiltersBS = new BehaviorSubject<PaginationParams>(this.state.getPartOfState('alarmService_filters'));
  private alarmFilters$ = this.alarmFiltersBS.asObservable();

  private worker = new Worker(new URL('./alarm-filter.worker', import.meta.url), {
    type: 'module',
    name: 'alarm-filter',
  });
  private comlink: Comlink.Remote<WorkerAlarmFilterFunctions> = Comlink.wrap<WorkerAlarmFilterFunctions>(this.worker);

  public filteredAlarms$ = combineLatest([
    this.alarms$,
    this.alarmFilters$
  ])
    .pipe(
      switchMap(([alarms, alarmFilters]) => this.comlink.filterAlarms(alarms, alarmFilters, this.authService.getTenant()))
    );

  public alarmsActiveForUser$ = this.alarms$
    .pipe(
      switchMap(alarms => this.comlink.filterUserdisabledAlarms(alarms))
    );

  public translate: any[] = [];
  public shortMsg = {
    'vrTrainConflict': _('Equipment conflict between trains %TRAIN1 and %TRAIN2.'),
    'vrShiftConflict': _('Personnel conflict between trains %TRAIN1 and %TRAIN2 (%ROLE).'),
  };
  public longMsgActive = {
    'vrTrainConflict': _('Equipment conflict between trains %TRAIN1 and %TRAIN2. Alarm was activated on %DATE and it´s still active.'),
    'vrShiftConflict': _('Personnel conflict between trains %TRAIN1 and %TRAIN2 (%ROLE). Alarm was activated on %DATE and it´s still active.'),
  };
  public longMsgDeActive = {
    'vrTrainConflict': _('Equipment conflict between trains %TRAIN1 and %TRAIN2. Alarm was activated on %DATE1 and it was deactivated on %DATE2'),
    'vrShiftConflict': _('Personnel conflict between trains %TRAIN1 and %TRAIN2 (%ROLE). Alarm was activated on %DATE1 and it was deactivated on %DATE2')
  };

  public static getIdentifier(alarm: BackendAlarm) {
    switch (alarm.type) {
      case 'trainDeparture':
      case 'vrTrainStopped':
      case 'vrOverheating':
        return alarm.payload.tripDate + '_' + alarm.payload.tripNumber;
      case 'vrTrainConflict':
      case 'vrShiftConflict':
        return alarm.payload.trainDate + '_' + alarm.payload.tripNumber;
      case 'vrWheelAlert':
      case 'vrVoltageAlert':
        return alarm.alarmId;
      case 'miproMeasure':
        return alarm.payload.p_deviceid;
      case 'valmetDNA':
        return alarm.payload.Tag;
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.worker.terminate();
  }

  public async PaginatedAlarmList(filters: PaginationParams): Promise<AbstractDataModel<BackendAlarm>> {

    this.setAlarmFilters(filters);

    let query = 'sort=-activatedAt';

    for (const s of filters.alarmTypes) {
      if (this.authService.getTenant() === 2) {
        query += '&type=' + s;
      } else {
        query += '&rank=' + s;
      }
    }
    if (filters.dates && filters.dates.selected_type !== 'All') {
      const from = filters.dates.startDate.toISOString();
      const to = filters.dates.endDate.toISOString();
      query += '&createdAt<' + to + '&createdAt>' + from;
    }
    const temp = await this.filterData(query);
    if (!temp.isOk) {
      console.warn(temp); // TODO Do something sensible with ServiceAnswer;
      return;
    }
    const tenant = this.authService.getTenant();
    const result = (await this.comlink.filterPaginatedAlarms(
      temp.data.data.filter(o => o.payload && o.activatedAt), filters, tenant
    ))
      .map(o => {
        if (o.payload) {
          if (o.activatedAt) {
            o.activatedAt = parseToDate(o.activatedAt as any);
          }
          if (o.deactivatedAt) {
            o.deactivatedAt = parseToDate(o.deactivatedAt as any);
          }
          if (o.createdAt) {
            o.createdAt = parseToDate(o.createdAt as any);
          }
          if (o.currentlyPlannedDeactivateAt) {
            o.currentlyPlannedDeactivateAt = parseToDate(o.currentlyPlannedDeactivateAt as any);
          }
          const message = this.getMessage(o);
          this.translate = this.setTranslateProperties(o);
          return new AlarmMessage(message, o.createdAt, o, o.active ? 'danger' : 'info', this.translate);
        }
      })
      ;
    this.paginatedAlarms.next({
      data: result.slice(filters.offset, filters.offset + filters.limit),
      total: result.length
    });
  }

  public getMessage(alarm: BackendAlarm, longFormat?: boolean) {
    switch (alarm.type) {
      case 'trainDeparture':
        return _('Train %TRAIN1 didn´t depart on schedule.');
      case 'vrTrainStopped':
        return _('Train %TRAIN1 is stopped on route.');
      case 'vrOverheating':
        if (alarm.payload.location) {
          return _('Overheating alarm for train %TRAIN1 at %LOCATION.');
        }
        return _('Overheating alarm for train %TRAIN1.');
      case 'vrWheelAlert':
        if (alarm.payload.location) {
          return _('Wheel alarm for train %TRAIN1 at %LOCATION.');
        }
        return _('Wheel alarm for train %TRAIN1.');
      case 'vrTrainConflict':
      case 'vrShiftConflict':
        let msg = this.shortMsg[alarm.type];
        if (longFormat) {
          console.log(alarm);
          alarm.activatedAt = parseToDate(alarm.activatedAt);
          alarm.deactivatedAt = parseToDate(alarm.deactivatedAt);
          this.translate.push({
            key: '%DATE1',
            value: format(alarm.activatedAt, 'dd.MM.yyyy HH:mm:ss')
          });
          msg = this.getDeactivationString(alarm.type, alarm.deactivatedAt);
        }
        return msg;
      case 'vrVoltageAlert':
        if (alarm.payload.location) {
          return _('Voltage alarm for locomotive %TRAIN1 at %LOCATION.');
        }
        return _('Voltage alarm for locomotive %TRAIN1.');
      case 'valmetDNA':
        return alarm.payload.EventDescription
          ? `${alarm.payload.TagDescription} (${alarm.payload.EventDescription})`
          : alarm.payload.TagDescription;
      case 'miproMeasure':
      default:
        return alarm.payload.p_alarmtagname;
    }
  }

  public getTranslations(): TranslateModel[] {
    return this.translate;
  }

  private getDeactivationString(type: 'vrTrainConflict' | 'vrShiftConflict', deactivated: Date | number) {
    const d = format(deactivated, 'dd.MM.yyyy HH:mm:ss');
    console.log(d);
    if (d === 'Invalid Date') {
      return this.longMsgActive[type];
    } else {
      this.translate.push({ key: '%DATE2', value: d });
      return this.longMsgDeActive[type];
    }
  }

  private async init() {
    console.log('Alarm service constructing');
    const list = this.alarms.getValue();
    this.alarms.next(list);
    const pagi = this.paginatedAlarms.getValue();
    this.paginatedAlarms.next(pagi);

    this.websocket.newWebsocketMessage$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.handleNewWebsocketMessage(data);
      }
      );

    this.initAlarmList();
  }

  public async initAlarmList() {
    console.log('Init alarm list');
    const results = (await this.listAlarms());
    const l = results.data
      .filter(o => o.payload && o.activatedAt)
      .map(o => {
        if (o.payload) {
          if (o.activatedAt) {
            o.activatedAt = parseToDate(o.activatedAt as any);
          }
          if (o.deactivatedAt) {
            o.deactivatedAt = parseToDate(o.deactivatedAt as any);
          }
          if (o.createdAt) {
            o.createdAt = parseToDate(o.createdAt as any);
          }
          if (o.currentlyPlannedDeactivateAt) {
            o.currentlyPlannedDeactivateAt = parseToDate(o.currentlyPlannedDeactivateAt as any);
          }
          const message = this.getMessage(o);
          this.translate = this.setTranslateProperties(o);
          return new AlarmMessage(message, o.activatedAt, o, o.active ? 'danger' : 'info', this.translate);
        }
      });
    this.alarms.next(l);
  }

  public setTranslateProperties(alarm: BackendAlarm) {
    switch (alarm.type) {
      case 'trainDeparture':
      case 'vrTrainStopped':
        return [{ key: '%TRAIN1', value: alarm.payload.tripNumber }];
      case 'vrWheelAlert':
      case 'vrOverheating':
      case 'vrVoltageAlert':
        if (alarm.payload.location) {
          return [
            { key: '%TRAIN1', value: alarm.alarmId },
            { key: '%LOCATION', value: alarm.payload.location }
          ];
        }
        return [{ key: '%TRAIN1', value: alarm.alarmId }];
      case 'vrTrainConflict':
      case 'vrShiftConflict':
        const role = getRoleGroupName(alarm.payload.personGroup, alarm.payload.personRole);
        if (!alarm.active) {
          return [
            { key: '%TRAIN1', value: alarm.payload.tripNumber },
            { key: '%TRAIN2', value: alarm.payload.dependentTripNumber },
            { key: '%ROLE', value: role },
            { key: '%DATE1', value: format(parseToDate(alarm.activatedAt), 'dd.MM.yyyy HH:mm:ss') },
            { key: '%DATE2', value: format(parseToDate(alarm.deactivatedAt), 'dd.MM.yyyy HH:mm:ss') }
          ];
        } else {
          return [
            { key: '%TRAIN1', value: alarm.payload.tripNumber },
            { key: '%TRAIN2', value: alarm.payload.dependentTripNumber },
            { key: '%ROLE', value: role },
            { key: '%DATE', value: format(parseToDate(alarm.activatedAt), 'dd.MM.yyyy HH:mm:ss') }];
        }
    }
  }

  private handleNewWebsocketMessage(msg: WebsocketMessage<BackendAlarm>) {
    //// this includes also miproData / VEJ originated alarms
    if (msg.target.includes('Alarms') || msg.target.includes('alarms')) {
      const list = this.alarms.getValue();
      if (msg.body.payload) {
        if (msg.body.activatedAt) {
          msg.body.activatedAt = parseToDate(msg.body.activatedAt);
        }
        if (msg.body.deactivatedAt) {
          msg.body.deactivatedAt = parseToDate(msg.body.deactivatedAt);
        }
        if (msg.body.createdAt) {
          msg.body.createdAt = parseToDate(msg.body.createdAt);
        }
        if (msg.body.currentlyPlannedDeactivateAt) {
          msg.body.currentlyPlannedDeactivateAt = parseToDate(msg.body.currentlyPlannedDeactivateAt);
        }
        this.translate = this.setTranslateProperties(msg.body);
        //// if alarmmessage already in list, remove and add to top/beginning..
        const almMsg = new AlarmMessage(this.getMessage(msg.body), msg.body.activatedAt, msg.body,
          msg.body.active ? 'danger' : 'info', this.translate);
        const existingAlmMsg = list.find((element) => element.message === this.getMessage(msg.body));
        const idx = list.indexOf(existingAlmMsg);
        if (idx !== -1) {
          list.splice(idx, 1);
        }
        list.unshift(almMsg);
        const alarmPassesFilters = this.comlink.checkIfAlarmPassesFilters(msg.body, this.alarmFiltersBS.getValue(), this.authService.getTenant());
        if (alarmPassesFilters) {
          this.appMessageService.broadcastAlarm(
            new AppMessage(true, this.getMessage(msg.body), msg.body.active ? 'error' : 'success', 'alarm-list', '', msg.body, this.translate));
        }
        this.alarms.next(list);
      } else if (msg.body.command === 'RELOAD') {
        console.warn(msg);
        this.initAlarmList();
      }
    }
  }

  public async listAlarms(): Promise<{ data: BackendAlarm[], total: number }> {
    if (this.authService.getTenant() === 2) {
      return this.http.get<{ data: BackendAlarm[], total: number }>(environment.dataApiURL +
        'alarms/?sort=-createdAt').toPromise();
    } else {
      //// TODO: limit is not best-bet here,
      //// if some alarm is active for some day's, it will eventually become invisible
      //// For example in Joensuu tenant, which is quite a bit "alarmy" tenant,
      //// ie. frequency of variety of different alarms is fast, active alarms will be kind of "hidden".
      return this.http.get<{ data: BackendAlarm[], total: number }>(environment.dataApiURL + getDataName(this.authService.getTenant()) +
        'Alarms/?sort=-createdAt&limit=1200').toPromise();
    }
  }

  public queryAlarms(query: string): Promise<ServiceAnswer<AbstractDataModel<BackendAlarm>>> {
    return this.filterData<BackendAlarm>(query);
  }

  /**
   * TODO: long format does not translate correctly to Finnish
   */
  public getTranslatedAlarm(alarm: BackendAlarm, longFormat: boolean) {
    const translationHelper = this.setTranslateProperties(alarm);

    let text = this.translateService.instant(this.getMessage(alarm, longFormat));
    if (translationHelper && translationHelper.length > 0) {
      for (const single of translationHelper) {
        text = text.replace(single.key, single.value);
      }
    }
    return text;
  }

  public setAlarmActiveStatus(alarmId: string, isActive: boolean) {
    return this.http.post(`${environment.regoApiURL}alarmmanipulator/toggle`,
      null,
      {
        params: {
          id: alarmId,
          manuallydisabled: isActive ? 'false' : 'true'
        }
      }
    );
  }

  private setAlarmFilters(filters: PaginationParams) {
    this.alarmFiltersBS.next(filters);
    this.state.updateState('alarmService_filters', filters);
  }

  public alarmsForFeature$(feature: MapElement<Geometry>): Observable<AlarmMessage[]> {
    const id: string = feature.properties[feature.layer.metadata.uniqueBy].toString();
    const ids$: Observable<Set<string>> = from(this.linkingService.getLinkingsForRegoId(id)).pipe(
      map(linkings => {
        const ids = new Set(linkings.map(linking => linking.sourceId));
        ids.add(id);
        return ids;
      }),
    );

    return this.alarmsActiveForUser$.pipe(
      withLatestFrom(ids$),
      map(([alarms, ids]) => {
        return alarms.filter(alarm => ids.has(AlarmService.getIdentifier(alarm.metadata)));
      }),
    );
  }
}
